import { Table, Spin, Button, Space, Input, notification } from "antd";
import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { saveAs } from "file-saver";
import { useSelector } from "react-redux";

import axiosInstance from "../../redux/apiAxios";
import {
  FileExcelOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { MAX_Leads, organizationProvider } from "../../utils/constants";
import DateRangeFilter from "../UI/DateRangeFilter";

const DetailedAnalysis = forwardRef(({ onDataLoaded, externalDateRange }, ref) => {
  const { organizationData} = useSelector(
    (state) => state.organization
  );
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [fetchingData, setFetchingData] = useState(false);
  const [selectedRange, setSelectedRange] = useState([
    {
      startDate: dayjs().startOf("day").toDate(),
      endDate: dayjs().endOf("day").toDate(),
      key: "selection",
    },
  ]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    },
  });
  const [totalleads, settotalleads] = useState(0);
  const [exportingData, setExportingData] = useState(false);

  const searchInput = useRef(null);

  useEffect(() => {
    const startDate = dayjs(selectedRange[0].startDate);
    const endDate = dayjs(selectedRange[0].endDate);
    fetchTodayAnalytics(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData, selectedRange]);

  const fetchTodayAnalytics = async (startDate, endDate) => {
    if (!organizationData?.apiKeys?.whatConverts_profileId) {
      notification.warning({
        message: "WhatConverts Profile ID Missing",
        description: (
          <Space direction="vertical">
            <span>Please configure your WhatConverts Profile ID in the organization settings.</span>
            <Button 
              type="primary" 
              onClick={() => navigate("/orgSettings?tab=Provider Info&section=3")}
            >
              Go to API Integration Settings
            </Button>
          </Space>
        ),
        duration: 0,
      });
      return;
    }

    const [start_date, end_date] = formatDateRange(startDate, endDate);

    setFetchingData(true);

    try {
      // Fetch ALL data at once for client-side filtering
      let allLeads = [];
      let currentPage = 1;
      let totalPages = 1;

      // First request to get total count
      const firstResponse = await axiosInstance.post(
        "/whatconverts/fetchAnalytics",
        {
          page: 1,
          profile_id: organizationData.apiKeys.whatConverts_profileId,
          start_date,
          end_date,
          leads_per_page: MAX_Leads,
        }
      );

      if(firstResponse?.data?.message){
        notification.success({
          message: firstResponse?.data?.message,
          description: firstResponse?.data?.details,
          duration: 0,
        });
        setFetchingData(false);
        return;
      }

      settotalleads(firstResponse.data.total_leads);
      totalPages = firstResponse.data.total_pages;
      allLeads.push(...firstResponse.data.leads);

      // Fetch remaining pages if any
      if (totalPages > 1) {
        const remainingRequests = [];
        for (let page = 2; page <= totalPages; page++) {
          remainingRequests.push(
            axiosInstance.post("/whatconverts/fetchAnalytics", {
              page,
              profile_id: organizationData.apiKeys.whatConverts_profileId,
              start_date,
              end_date,
              leads_per_page: MAX_Leads,
            })
          );
        }
        
        const remainingResponses = await Promise.all(remainingRequests);
        remainingResponses.forEach(response => {
          allLeads.push(...response.data.leads);
        });
      }

      // Transform all data
      const transformedData = allLeads.map((item, index) => {
        return {
          key: item.lead_id || index, // Unique key for React
          date_created: item.date_created,
          "additional_fields.Summary": item.additional_fields?.Summary || null,
          lead_status: item.lead_status,
          "mapped_fields.Booked Campaign": item.additional_fields?.["Campaign Name"] || "Not Selected",
          lead_source: item.lead_source,
          lead_medium: item.lead_medium,
          "mapped_fields.Type": item.additional_fields?.Type || null,
          "mapped_fields.Business Unit": item.additional_fields?.["Business Unit Name"] || "Not Selected",
          "mapped_fields.Job Type": item.additional_fields?.["Job Type Name"] || "Unbooked",
          "lead_campaign": item.lead_campaign || "(not set)",
          "additional_fields.Phone Number": item.additional_fields?.["Phone Number"] || null,
          "additional_fields.Contact Name": item.additional_fields?.["Contact Name"] || null,
          "additional_fields.Lead Type": item.additional_fields?.["Lead Type"] || null,
        };
      });

      setData(transformedData);
      
      // Reset pagination to page 1 when data changes
      setTableParams({
        pagination: {
          current: 1,
          pageSize: 25,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        },
      });
      
      setFetchingData(false);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setFetchingData(false);
    }
  };

  const formatDateRange = (startDate, endDate) => {
    const start_date = startDate.format("YYYY-MM-DDTHH:mm:ss[Z]");
    const end_date = endDate.endOf('day').format("YYYY-MM-DDTHH:mm:ss[Z]");
    return [start_date, end_date];
  };

  const handleDateChange = async (ranges) => {
    const { selection } = ranges;
    setSelectedRange([selection]);
  };

  const togglePicker = () => {
    setPickerOpen(!pickerOpen);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // Just update pagination state - Ant Design handles the rest
    setTableParams({
      pagination,
      filters,
      sorter,
    });
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const exportToExcel = async () => {
    console.log('DetailedAnalysis exportToExcel called, data length:', data?.length);
    if (!data || data.length === 0) {
      console.log('No data to export');
      return;
    }
    
    setExportingData(true);
    try {
      const startDate = dayjs(selectedRange[0].startDate);
      const endDate = dayjs(selectedRange[0].endDate);
      const [start_date, end_date] = formatDateRange(startDate, endDate);

      // Use the data already loaded in state
      const allData = data.map((item) => {
        return {
          Time: dayjs.utc(item.date_created).format("ddd D MMMM YYYY, h:mm A"),
          "Job ID": item["additional_fields.Summary"] || "Unbooked",
          "Lead Status": item.lead_status,
          "Booked Campaign": item["mapped_fields.Booked Campaign"],
          "Lead Source": item.lead_source,
          "Lead Medium": item.lead_medium,
          "Lead Campaign": item.lead_campaign,
          Type: item["mapped_fields.Type"],
          "Business Unit": item["mapped_fields.Business Unit"],
          "Job Type": item["mapped_fields.Job Type"],
          "Phone Number": item["additional_fields.Phone Number"],
          "Contact Name": item["additional_fields.Contact Name"],
          "Lead Type": item["additional_fields.Lead Type"],
        };
      });

      const ws = XLSX.utils.json_to_sheet(allData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Analytics");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const dataBlob = new Blob([excelBuffer], { type: EXCEL_TYPE });
      const sheetName = `${start_date.split('T')[0]}_${end_date.split("T")[0]}`;
      saveAs(dataBlob, `Analytics_Report_${sheetName}.xlsx`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setExportingData(false);
    }
  };

  const reloadData = () => {
    const start = dayjs(selectedRange[0].startDate);
    const end = dayjs(selectedRange[0].endDate);
    fetchTodayAnalytics(start, end);
  };

  const EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

  const columns = [
    {
      title: "Time",
      dataIndex: "date_created",
      key: "date_created",
      align: "center",
      width: 140,
      render: (date) => {
        const localTime = dayjs.utc(date).local()
        return localTime.format("MMM D, h:mm A");
      },
      sorter: (a, b) => dayjs(a.date_created).unix() - dayjs(b.date_created).unix(),
    },
    {
      title: "Job ID",
      dataIndex: "additional_fields.Summary",
      key: "Summary",
      align: "center",
      width: 100,
      render: (text, record) => {
        const isHCp = organizationData?.organization_provider === organizationProvider.HCP
        let href = isHCp ? `https://pro.housecallpro.com/app/jobs/${text}` : `https://go.servicetitan.com/#/Job/Index/${text}`
        return text ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: '12px' }}
          >
            {text}
          </a>
        ) : (
          <span style={{ fontSize: '12px' }}>Unbooked</span>
        );
      },
      sorter: (a, b) => {
        const aVal = a["additional_fields.Summary"] || 0;
        const bVal = b["additional_fields.Summary"] || 0;
        return aVal - bVal;
      },
    },
    {
      title: "Customer",
      dataIndex: "additional_fields.Contact Name",
      align: "center",
      key: "Contact Name",
      width: 120,
      ...getColumnSearchProps("additional_fields.Contact Name"),
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "additional_fields.Lead Type",
      align: "center",
      key: "Lead Type",
      width: 100,
      filters: [
        { text: "New Customer", value: "New Customer" },
        { text: "Existing Customer", value: "Existing Customer" },
      ],
      onFilter: (value, record) => record["additional_fields.Lead Type"] === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "additional_fields.Phone Number",
      align: "center",
      key: "Phone Number",
      width: 100,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "lead_status",
      align: "center",
      key: "lead_status",
      width: 80,
      filters: [
        { text: "Repeat", value: "Repeat" },
        { text: "Unique", value: "Unique" },
      ],
      onFilter: (value, record) => record.lead_status === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text}</span>
      ),
    },
    {
      title: "ST Campaign",
      dataIndex: "mapped_fields.Booked Campaign",
      key: "Booked Campaign",
      align: "center",
      width: 120,
      ...getColumnSearchProps("mapped_fields.Booked Campaign"),
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
    {
      title: "Source",
      dataIndex: "lead_source",
      key: "lead_source",
      align: "center",
      width: 80,
      filters: [
        { text: "google", value: "google" },
        { text: "(direct)", value: "(direct)" },
        { text: "yahoo", value: "yahoo" },
        { text: "bing", value: "bing" },
      ],
      onFilter: (value, record) => record.lead_source === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text}</span>
      ),
    },
    {
      title: "Medium",
      dataIndex: "lead_medium",
      key: "lead_medium",
      align: "center",
      width: 80,
      filters: [
        { text: "organic", value: "organic" },
        { text: "(none)", value: "(none)" },
        { text: "cpc", value: "cpc" },
      ],
      onFilter: (value, record) => record.lead_medium === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text}</span>
      ),
    },
    {
      title: "Campaign",
      dataIndex: "lead_campaign",
      key: "lead_campaign",
      align: "center",
      width: 120,
      filters: Array.from(new Set(data.map(item => item.lead_campaign)))
        .sort()
        .map(value => ({
          text: value,
          value: value
        })),
      onFilter: (value, record) => record.lead_campaign === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
    {
      title: "Scheduler Type",
      dataIndex: "mapped_fields.Type",
      key: "Type",
      align: "center",
      width: 120,
      filters: [
        { text: "Scheduler Abandoned", value: "Scheduler Abandoned" },
        { text: "Scheduler Started", value: "Scheduler Started" },
        { text: "Scheduler Finished", value: "Scheduler Finished" },
      ],
      onFilter: (value, record) => record["mapped_fields.Type"] === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
    {
      title: "Business Unit",
      dataIndex: "mapped_fields.Business Unit",
      key: "Business Unit",
      align: "center",
      width: 120,
      filters: Array.from(new Set(data.map(item => item['mapped_fields.Business Unit'])))
        .sort()
        .map(value => ({
          text: value,
          value: value
        })),
      onFilter: (value, record) =>
        record["mapped_fields.Business Unit"] === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
    {
      title: "Job Type",
      dataIndex: "mapped_fields.Job Type",
      key: "Job Type",
      align: "center",
      width: 100,
      filters: Array.from(new Set(data.map(item => item['mapped_fields.Job Type'])))
        .sort()
        .map(value => ({
          text: value,
          value: value
        })),
      onFilter: (value, record) =>
        record["mapped_fields.Job Type"] === value,
      render: (text) => (
        <span style={{ fontSize: '12px' }}>{text || '-'}</span>
      ),
    },
  ];

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsidePicker = pickerRef.current && pickerRef.current.contains(event.target);
      if (!isClickInsidePicker) {
        setPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    exportToExcel,
    detailedAnalysisData: data.length > 0 ? data : [],
    reloadData,
  }));

  useEffect(() => {
    if (data.length > 0 && onDataLoaded) {
      onDataLoaded();
    }
  }, [data, onDataLoaded]);

  return (
    <Spin spinning={fetchingData}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <DateRangeFilter
          selectedRange={selectedRange}
          togglePicker={togglePicker}
          pickerOpen={pickerOpen}
          handleDateChange={handleDateChange}
          pickerRef={pickerRef}
          label="Filter by Date Range"
          containerStyle={{
            minWidth: '200px'
          }}
          dropdownStyle={{
            minWidth: '600px',
            right: '0'
          }}
        />
      </div>
      <Table
        columns={columns}
        dataSource={data}
        pagination={tableParams.pagination}
        onChange={handleTableChange}
        rowKey={(record) => record.key}
        scroll={{ x: 1200 }}
        size="small"
      />
    </Spin>
  );
});

export default DetailedAnalysis;